'use client';
import { jsx } from 'react/jsx-runtime';
import { usePreventScroll } from '@react-aria/overlays';
import { useState, useRef, useCallback, useEffect } from 'react';
import { useComponentStylesLoaded } from '../../hooks/use-component-styles-loaded.js';
import { useTransition } from '../../hooks/use-transition.js';
import { ModalDialogContext } from './modal-dialog.context.js';
import { getComponentConfigProvider } from '../../contexts/internal/component-config/component-config.component.js';
import { isFunction } from '../../../core/utilities/is.utils.js';
import { filterProps } from '../core/core.utils.js';
import { renderWithRef } from '../core/render-with-ref.js';
import { SWAN_STYLE_KEY_MAP } from '../head/stylesheets.component.js';
import { assignRefs } from '../../../core/utilities/ref.utils.js';
import { processStyleProps } from '../../../core/utilities/swan-style.js';
import { RenderComp } from '../core/render-comp.component.js';

function _object_without_properties(e, o) {
    if (null == e) return {};
    var r, t, n = _object_without_properties_loose(e, o);
    if (Object.getOwnPropertySymbols) {
        var l = Object.getOwnPropertySymbols(e);
        for(t = 0; t < l.length; t++)r = l[t], !(o.indexOf(r) >= 0) && Object.prototype.propertyIsEnumerable.call(e, r) && (n[r] = e[r]);
    }
    return n;
}
function _object_without_properties_loose(e, o) {
    if (null == e) return {};
    var r, t, n = {}, l = Object.keys(e);
    for(t = 0; t < l.length; t++)r = l[t], o.indexOf(r) >= 0 || (n[r] = e[r]);
    return n;
}
let [h, w, b] = getComponentConfigProvider('Dialog');
const Dialog = renderWithRef('Dialog', null, (u, p)=>{
    useComponentStylesLoaded('Dialog', SWAN_STYLE_KEY_MAP.modalDialog);
    let { variant: w = 'standard', onRequestDismiss: b, takeOver: y = false, bodyWidth: M = 'standard', isOpen: D = false, children: _, isModal: k = false, onlyRenderWhenOpen: O = false, bgc: E, backgroundColor: j } = u, x = _object_without_properties(u, [
        "variant",
        "onRequestDismiss",
        "takeOver",
        "bodyWidth",
        "isOpen",
        "children",
        "isModal",
        "onlyRenderWhenOpen",
        "bgc",
        "backgroundColor"
    ]), [C, L] = useState(!!D), [I, P] = useState(void 0), [R, S] = useState(void 0), [W, H] = useState('undefined' != typeof window && void 0 !== window.HTMLDialogElement), T = useTransition(!!D);
    usePreventScroll({
        isDisabled: !C
    });
    let q = useRef(null), B = useRef(null), N = assignRefs(p, q), z = useCallback(()=>{
        var e, o;
        (null === (e = q.current) || void 0 === e ? void 0 : e.open) && (null === (o = q.current) || void 0 === o || o.close());
    }, []), A = useCallback((e)=>{
        L(false), isFunction(b) && e.target === q.current && b();
    }, [
        b
    ]);
    useEffect(()=>{
        W && q.current && (D ? (L(true), k ? q.current.showModal() : q.current.show()) : z());
    }, [
        D,
        k,
        W,
        z
    ]), useEffect(()=>{
        let e;
        let o = q.current;
        return o && !window.HTMLDialogElement && (e = setInterval(()=>{
            let r = window.dialogPolyfill;
            r && (r.registerDialog(o), H(true), clearInterval(e));
        }, 200)), ()=>{
            e && clearInterval(e);
        };
    }, []), useEffect(()=>{
        let e = q.current, o = false, handleMouseDown = ()=>{
            o = false;
        }, handleMouseMove = ()=>{
            o = true;
        }, handleClick = (e)=>{
            k && e.target === q.current && !o && z();
        };
        if (e) return e.addEventListener('mousedown', handleMouseDown), e.addEventListener('mousemove', handleMouseMove), e.addEventListener('click', handleClick), e.addEventListener('close', A), ()=>{
            e.removeEventListener('mousedown', handleMouseDown), e.removeEventListener('mousemove', handleMouseMove), e.removeEventListener('click', handleClick), e.removeEventListener('close', A);
        };
    }, [
        z,
        A,
        k
    ]);
    let F = new Set([
        'swan-dialog',
        'swan-vanilla-ignore'
    ]);
    k || F.add('swan-dialog-modeless');
    let G = processStyleProps(x), J = filterProps(x, [
        'backgroundColor',
        'bgc'
    ], G);
    return J.role = 'dialog', J['aria-modal'] = k ? 'true' : 'false', x['aria-label'] || x['aria-labelledby'] || !I || (J['aria-labelledby'] = I), jsx(ModalDialogContext.Provider, {
        value: {
            variant: w,
            transitionState: T,
            handleModalCloseButton: z,
            titleId: I,
            setTitleId: P,
            bgc: E,
            backgroundColor: j,
            takeOver: null != y && y,
            isModal: null != k && k,
            bodyWidth: M || 'standard',
            footerPinned: R,
            setFooterPinned: S
        },
        children: jsx(h, {
            config: {
                inModal: true,
                portal: false,
                portalRef: B,
                dialogRef: q
            },
            children: jsx(RenderComp, {
                root: "dialog",
                forwardedRef: N,
                classNames: F,
                props: J,
                children: !O || D ? _ : null
            })
        })
    });
});

export { Dialog, b as DialogConfigContext, w as useInternalDialogConfigContext };
